import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridOptions } from 'ag-grid-community';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { map } from 'rxjs/operators';

import { myCustomTheme } from '@constants/grid-themes';
import { getMipsBlockReasonName } from '@constants/mips-block-reasons';
import { OrganizationMipsSubmissionStatus } from '@models/mips-submission-block.model';
import { MipsSubmissionBlockActions } from '@store/actions/mips-submission-block/mips-submission-block.actions';
import {
  selectOrganizationSubmissionStatuses,
  selectMipsSubmissionBlockLoading,
} from '@store/selectors/mips-submission-block/mips-submission-block.selectors';
import { ConfirmationModalComponent } from '@components/confirmation-modal/confirmation-modal.component';
import {
  MipsSubmissionBlockModalComponent,
  MipsSubmissionBlockModalData,
} from '@components/modals/mips-submission-block-modal/mips-submission-block-modal.component';
import { MipsSubmissionBulkBlockModalComponent } from '@components/modals/mips-submission-bulk-block-modal/mips-submission-bulk-block-modal.component';
import { MipsSubmissionActionsRendererComponent } from './mips-submission-actions-renderer.component';

ModuleRegistry.registerModules([AllCommunityModule]);

@Component({
  selector: 'app-mips-submission-admin',
  standalone: true,
  imports: [CommonModule, AgGridAngular, MatButtonModule],
  templateUrl: './mips-submission-admin.component.html',
  styleUrl: './mips-submission-admin.component.css',
})
export class MipsSubmissionAdminComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly dialog = inject(MatDialog);

  theme = myCustomTheme;

  rowData$ = this.store.select(selectOrganizationSubmissionStatuses).pipe(
    map((statuses) => this.sortDefault(statuses))
  );
  loading$ = this.store.select(selectMipsSubmissionBlockLoading);

  public gridOptions: GridOptions = {
    pagination: true,
    paginationPageSize: 20,
    paginationPageSizeSelector: [10, 20, 50],
    suppressClickEdit: true,
  };

  colDefs: ColDef[] = [
    { field: 'organizationName', headerName: 'Organization Name', flex: 2, minWidth: 160 },
    { field: 'legacyOrganizationId', headerName: 'ID', flex: 0.6, minWidth: 70 },
    {
      headerName: 'Status',
      flex: 0.8,
      minWidth: 90,
      valueGetter: (params: any) => (params.data?.isBlocked ? 'Blocked' : 'Unblocked'),
    },
    {
      headerName: 'Blocked By',
      flex: 0.9,
      minWidth: 90,
      valueGetter: (params: any) => params.data?.blockedByUserName ?? 'N/A',
    },
    {
      headerName: 'Test Org',
      flex: 0.7,
      minWidth: 80,
      valueGetter: (params: any) => (params.data?.isTestOrganization ? 'Test Org' : 'Not Test'),
    },
    {
      headerName: 'Reason',
      flex: 2.2,
      minWidth: 140,
      valueGetter: (params: any) => this.formatReasons(params.data?.reasonCodes),
    },
    {
      headerName: 'Actions',
      colId: 'actions',
      minWidth: 140,
      width: 140,
      pinned: 'right',
      filter: false,
      sortable: false,
      resizable: false,
      tooltipValueGetter: () => '',
      cellRenderer: MipsSubmissionActionsRendererComponent,
    },
  ];

  defaultColDef: ColDef = {
    filter: true,
    resizable: true,
    sortable: true,
    editable: false,
    minWidth: 80,
    tooltipValueGetter: (params: any) => (params.value != null ? String(params.value) : ''),
  };

  ngOnInit(): void {
    this.store.dispatch(MipsSubmissionBlockActions.loadOrganizationStatuses());
  }

  onCellClicked(params: any): void {
    if (params.column.colId !== 'actions') return;
    const action = params.event?.target?.dataset?.action;
    if (!action) return;
    const status = params.data as OrganizationMipsSubmissionStatus;

    if (action === 'edit' || action === 'block') {
      this.openBlockModal(status);
    } else if (action === 'unblock') {
      this.confirmUnblock(status);
    }
  }

  openBulkUpdateModal(): void {
    this.dialog.open(MipsSubmissionBulkBlockModalComponent, {
      width: '600px',
      disableClose: true,
    });
  }

  private openBlockModal(status: OrganizationMipsSubmissionStatus): void {
    const data: MipsSubmissionBlockModalData = {
      organizationId: status.organizationId,
      organizationName: status.organizationName,
      selectedReasonCodes: [...status.reasonCodes],
    };

    this.dialog.open(MipsSubmissionBlockModalComponent, {
      width: '480px',
      disableClose: true,
      data,
    });
  }

  private confirmUnblock(status: OrganizationMipsSubmissionStatus): void {
    const dialogRef = this.dialog.open(ConfirmationModalComponent, {
      data: {
        title: 'Unblock MIPS Submission',
        content: `Are you sure you want to unblock "${status.organizationName}"?`,
        confirmText: 'Unblock',
        cancelText: 'Close',
      },
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.store.dispatch(
          MipsSubmissionBlockActions.unblockOrganization({ organizationId: status.organizationId })
        );
      }
    });
  }

  private formatReasons(reasonCodes: string[] | undefined): string {
    if (!reasonCodes || reasonCodes.length === 0) return 'N/A';
    return reasonCodes.map((code) => getMipsBlockReasonName(code)).join(', ');
  }

  private sortDefault(statuses: OrganizationMipsSubmissionStatus[]): OrganizationMipsSubmissionStatus[] {
    return [...statuses].sort((first, second) => {
      if (first.isBlocked !== second.isBlocked) {
        return first.isBlocked ? -1 : 1;
      }
      return (first.legacyOrganizationId ?? '').localeCompare(second.legacyOrganizationId ?? '', undefined, { numeric: true });
    });
  }
}
